import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DataTable } from '@elliemae/ds-data-table';
import { DSButtonV2 } from '@elliemae/ds-button-v2';
import { Grid } from '@elliemae/ds-grid';
import { DSPageHeaderV2, PageHeaderTitle } from '@elliemae/ds-page-header';
import { DSPagination } from '@elliemae/ds-pagination';
import { DSTypography, TYPOGRAPHY_VARIANTS } from '@elliemae/ds-typography';
import { DecisionDialog } from '@frontend/elliemae-helpers';
import { useElementSize } from '@frontend/utils';
import { EnvNotificationHeader } from '../../components/Header/EnvNotificationHeader';
import {
  deleteScenario,
  duplicateScenario,
  getScenarios,
} from '../../../common/services/loanScenarioServices';
import { openGlobalErrorModalAction } from '../../../data/globalErrorModal/actionCreators';
import { closeLoader, openLoader } from '../../../data/screenLoader/actions';
import { getColumns } from './columns';
import { DashboardGlobalHeader } from './DashboardGlobalHeader';
import { EmptyScenarios } from './EmptyScenarios';
import { DashboardLayout, TableViewport } from './styled';

export type ScenarioRow = {
  id: string;
  loanId: string;
  name: string;
  channelType: string;
  ltv: string;
  fico: string;
  occupancy: string;
  createdDate: string;
  modifiedDate: string;
};

type ScenarioDashboardProps = {
  onNew?: () => void;
};

const noop = () => {};
const PAGE_SIZE_OPTIONS = [20, 40, 60];

// Saved scenarios grid with New, Duplicate and Delete; shows a tip when nothing is saved.
export const ScenarioDashboard: React.FC<ScenarioDashboardProps> = ({
  onNew = noop,
}) => {
  const dispatch = useDispatch();
  const [scenarios, setScenarios] = useState<ScenarioRow[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ScenarioRow | null>(null);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  // DataTable only scrolls internally when given a pixel height.
  const [viewportRef, viewportSize] = useElementSize();

  const pageCount = Math.max(1, Math.ceil(scenarios.length / pageSize));

  // A delete can empty the last page, so step back to the new last page.
  useEffect(() => {
    if (pageIndex > pageCount) setPageIndex(pageCount);
  }, [pageIndex, pageCount]);

  // Runs an optional action then reloads the list behind the screen loader.
  const reloadAfter = useCallback(
    async (
      message: string,
      errorTitle: string,
      action?: () => Promise<void>,
    ) => {
      dispatch(openLoader(message));
      try {
        if (action) await action();
        setScenarios(await getScenarios());
      } catch {
        dispatch(
          openGlobalErrorModalAction({
            title: errorTitle,
            errorMessage: 'Something went wrong. Please try again.',
          }) as any,
        );
      } finally {
        setHasLoaded(true);
        dispatch(closeLoader());
      }
    },
    [dispatch],
  );

  useEffect(() => {
    reloadAfter('Loading Scenarios', 'Unable to Load Scenarios');
  }, [reloadAfter]);

  const handleDuplicate = useCallback(
    (row: ScenarioRow) =>
      reloadAfter('Duplicating Scenario', 'Unable to Duplicate Scenario', () =>
        duplicateScenario(row.id),
      ),
    [reloadAfter],
  );

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;
    setPendingDelete(null);
    reloadAfter('Deleting Scenario', 'Unable to Delete Scenario', () =>
      deleteScenario(id),
    );
  };

  const pageRows = useMemo(
    () => scenarios.slice((pageIndex - 1) * pageSize, pageIndex * pageSize),
    [scenarios, pageIndex, pageSize],
  );

  const columns = useMemo(
    () =>
      getColumns({
        onDuplicate: handleDuplicate,
        onDelete: setPendingDelete,
        boundaryRef: viewportRef,
      }),
    [handleDuplicate, viewportRef],
  );

  const hasScenarios = scenarios.length > 0;
  const pagination = useMemo(
    () =>
      hasScenarios && (
        <DSPagination
          width="100%"
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={pageCount}
          perPageOptions={PAGE_SIZE_OPTIONS}
          canPreviousPage={pageIndex > 1}
          canNextPage={pageIndex < pageCount}
          onPageChange={setPageIndex}
          onPreviousPage={() => setPageIndex((page) => page - 1)}
          onNextPage={() => setPageIndex((page) => page + 1)}
          onPageSizeChange={(size: number) => {
            setPageSize(size);
            setPageIndex(1);
          }}
        />
      ),
    [hasScenarios, pageIndex, pageSize, pageCount],
  );

  return (
    <DashboardLayout width="100%" height="100vh" rows={['auto', '1fr']}>
      <Grid>
        <EnvNotificationHeader />
        <DashboardGlobalHeader />
      </Grid>
      <Grid rows={['auto', '1fr', 'auto']} px="s" minHeight={0}>
        <DSPageHeaderV2
          pageTitle={<PageHeaderTitle>Scenarios</PageHeaderTitle>}
          toolbar={
            <DSButtonV2 buttonType="outline" onClick={onNew}>
              New
            </DSButtonV2>
          }
        />
        <TableViewport ref={viewportRef}>
          <DataTable
            colsLayoutStyle="auto"
            columns={columns}
            data={pageRows}
            height={viewportSize.height ? `${viewportSize.height}px` : '100%'}
            noResultsMessage="Click on New to Create a New Pricing Scenario"
            noResultsPlaceholder={
              hasLoaded ? <EmptyScenarios onNew={onNew} /> : <span />
            }
          />
        </TableViewport>
        {pagination}
      </Grid>
      <DecisionDialog
        isOpen={!!pendingDelete}
        toggleDialog={() => setPendingDelete(null)}
        onAffirmation={handleConfirmDelete}
        affirmationButtonLabel="Delete"
        denialButtonLabel="Cancel"
      >
        <DSTypography variant={TYPOGRAPHY_VARIANTS.B1}>
          {`Are you sure you want to delete "${
            pendingDelete?.name ?? ''
          }"? This cannot be undone.`}
        </DSTypography>
      </DecisionDialog>
    </DashboardLayout>
  );
};

export default ScenarioDashboard;
